import {
  Box,
  Radio,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";
import { MAX_UPLOAD_FILE_SIZE } from "../../constants/form";
import FileUtils from "../../utils/fileUtils";
import alertService from "../alert/alertService";
import { Trans, useTranslation } from "react-i18next";
import InfoPopper from "../popper/InfoPopper";
import { common, input, report } from "../../i18n";
import DocumentUploadIcon from "../icon/DocumentUploadIcon";
import { GridColDef } from "@mui/x-data-grid";
import { AppDataGrid } from "../datagrid/AppDataGrid";
import { EvidenceService } from "../../service/evidence.service";
import { EvidenceItem } from "../../models/Evidence";
import dayjs from "dayjs";
import { CustomGridToolbarQuickFilter } from "../datagrid/CustomGridToolbarQuickFilter";
import { CustomAutoComplete } from "./CustomAutoComplete";
import { useForm } from "react-hook-form";
import Grid2 from "@mui/material/Unstable_Grid2";
import { SubOrganizations } from "../../models/SubOrganization";
import { SelectedFile } from "./SelectedFile";
import { tableStyle } from "../../pages/emission/_components/data-table/EmissionDataTableStyle";

export type FilterFileType = "all" | "IMAGE" | "DOCUMENT";

type SelectedOption = "upload" | "select";

type Props = {
  attachment: string;
  setAttachmentValue: (value: string) => void;
  headline: string;
  description: string;
  attachmentRef: string;
  setAttachmentRefValue: (value: string) => void;
  subOrganization?: SubOrganizations;
  year: number;
};

type FileFilterForm = {
  type: FilterFileType;
};

export const UploadFileZone = (props: Props) => {
  const { t, i18n } = useTranslation([common, input, report], {
    nsMode: "fallback",
  });

  const deleteFile = () => {
    props.setAttachmentValue("");

    props.setAttachmentRefValue("");
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDropRejected(fileRejections, event) {
      if (fileRejections[0].errors[0].code === "file-too-large") {
        return alertService.error(
          t("maximum-file-size", {
            size: (MAX_UPLOAD_FILE_SIZE / 1e6).toFixed(0),
          }),
        );
      } else {
        return alertService.error(t("Invalid-File-Type"));
      }
    },
    async onDrop(acceptedFiles, fileRejections, event) {
      await FileUtils.getBase64(acceptedFiles[0])
        .then((res) => {
          props.setAttachmentValue(res as string);
        })
        .catch((err) => {
          props.setAttachmentValue("");
        });
    },
    maxFiles: 1,
    maxSize: MAX_UPLOAD_FILE_SIZE,
    multiple: false,
    accept: {
      "image/*": [],
      "application/pdf": [],
      "text/csv": [],
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [],
      "application/vnd.ms-excel": [],
      "application/msword": [],
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
        [],
    },
  });

  const [selectedOption, setSelectedOption] =
    useState<SelectedOption>("upload");

  const handleChange = async (
    event: React.MouseEvent<HTMLElement>,
    newSelection: SelectedOption | null,
  ) => {
    if (!newSelection) return;
    setSelectedOption(newSelection as SelectedOption);
  };

  const columns: GridColDef<EvidenceItem>[] = [
    {
      field: "select",
      headerName: "",
      width: 70,
      disableColumnMenu: true,
      sortable: false,
      align: "center",
      renderCell: (params) => {
        return (
          <Radio checked={props.attachmentRef === params.row.attachmentRef} />
        );
      },
    },
    {
      field: "evidenceFileName",
      headerName: t("File-Name"),
      width: 160,
      flex: 1,
      disableColumnMenu: true,
      renderCell: (params) => {
        return (
          <Tooltip title={t("Preview")}>
            <Box width={"100%"}>
              <Typography
                variant="text1"
                sx={{
                  textDecoration: "underline",
                  cursor: "pointer",
                }}
                onClick={() => openEvidence(params.row.attachmentRef)}
              >
                {params.value}
              </Typography>
            </Box>
          </Tooltip>
        );
      },
    },
    {
      field: "evidenceFileType",
      headerName: t("Type"),
      width: 180,
      disableColumnMenu: true,
    },
    {
      field: "evidenceFileSize",
      headerName: t("Size"),
      width: 180,
      disableColumnMenu: true,
      type: "number",
      renderCell: (params) => {
        const k = 1024;

        const sizes = ["Bytes", "KB", "MB", "GB", "TB"];

        const i = Math.floor(Math.log(params.value) / Math.log(k));

        const size = parseFloat((params.value / Math.pow(k, i)).toFixed(2));

        return `${size} ${sizes[i]}`;
      },
    },
    {
      field: "createdAt",
      headerName: t("Upload-Date"),
      width: 180,
      disableColumnMenu: true,
      valueFormatter: (value) =>
        dayjs(value).locale(i18n.language).format("DD MMM YYYY"),
    },
  ];

  const [rows, setRows] = useState<EvidenceItem[]>([]);

  const getEvidencesBySubOrgAndYear = async (): Promise<void> => {
    if (props.subOrganization) {
      const result = await EvidenceService.getEvidencesBySubOrgAndYear(
        props.subOrganization.refId,
        props.year,
      );

      setRows(
        result.sort((a, b) => dayjs(b.createdAt).diff(a.createdAt)) ?? [],
      );
    }
  };

  const openEvidence = async (attachmentRef: string): Promise<void> => {
    try {
      const file = await EvidenceService.getAttachmentByRef(attachmentRef);

      if (file) {
        window.open(file.evidenceS3Url);
      }
    } catch (error) {
      alertService.info(t("Cannot-Preview-File"));
    }
  };

  const { control, watch } = useForm<FileFilterForm>({
    reValidateMode: "onChange",
    defaultValues: {
      type: "all",
    },
  });

  const filterFileType = watch("type");

  const Toolbar = () => {
    return (
      <Grid2
        container
        disableEqualOverflow
        marginBottom={"20px"}
        alignItems={"end"}
        spacing={"20px"}
      >
        <Grid2 xs={12} sm={"auto"}>
          <Box minWidth={"200px"}>
            <CustomAutoComplete
              name={"type"}
              control={control}
              rules={undefined}
              label={t("Type")}
              info={""}
              placeholder={""}
              showLabelInput={false}
              listMenuItem={[
                {
                  nameEN: t("All"),
                  nameTH: t("All"),
                  value: "all",
                },
                {
                  nameEN: t("IMAGE"),
                  nameTH: t("IMAGE"),
                  value: t("IMAGE"),
                },
                {
                  nameEN: t("DOCUMENT"),
                  nameTH: t("DOCUMENT"),
                  value: t("DOCUMENT"),
                },
              ]}
            />
          </Box>
        </Grid2>
        <Grid2 xs={12} sm={"auto"} marginBottom={1}>
          <CustomGridToolbarQuickFilter />
        </Grid2>
        <Grid2 xs={12}>
          <SelectedFile
            attachment={props.attachment}
            attachmentRef={props.attachmentRef}
            deleteFile={deleteFile}
            evidenceList={rows}
          />
        </Grid2>
      </Grid2>
    );
  };

  useEffect(() => {
    if (selectedOption === "select" && props.year) {
      getEvidencesBySubOrgAndYear();
    }
  }, [selectedOption, props.year]);

  return (
    <Box minHeight={"110px"} width={"100%"}>
      <Typography
        variant="h3"
        color={"var(--dark)"}
        sx={{
          marginBottom: "8px",
          borderBlockStart: "1px",
        }}
      >
        {`${props.headline}`}
        <InfoPopper text={props.description} />
      </Typography>

      <ToggleButtonGroup
        value={selectedOption}
        exclusive
        onChange={handleChange}
        aria-label="file selection"
        sx={{
          borderRadius: "4px",
          overflow: "hidden",
          marginBottom: "20px",
        }}
      >
        <ToggleButton
          value="upload"
          sx={{
            "&.Mui-selected": {
              backgroundColor: "var(--green) !important",
              color: "white !important",
            },
          }}
        >
          {t("Upload-New-File")}
        </ToggleButton>
        <ToggleButton
          value="select"
          sx={{
            "&.Mui-selected": {
              backgroundColor: "var(--green) !important",
              color: "white !important",
            },
          }}
        >
          {t("Select-Existing-File")}
        </ToggleButton>
      </ToggleButtonGroup>

      {selectedOption === "upload" && (
        <>
          {(props.attachment && props.attachment !== "") ||
          (props.attachmentRef && props.attachmentRef !== "") ? (
            <SelectedFile
              attachment={props.attachment}
              attachmentRef={props.attachmentRef}
              deleteFile={deleteFile}
              evidenceList={rows}
            />
          ) : (
            <Box
              {...getRootProps()}
              sx={{
                border: "2px dashed grey",
                borderRadius: "8px",
                padding: "16px",
                textAlign: "center",
                cursor: "pointer",
                backgroundColor: isDragActive ? "#f0f0f0" : "#fafafa",
                "&:hover": { backgroundColor: "#f0f0f0" },
              }}
            >
              <input {...getInputProps()} />

              <Box color={"var(--gray4)"}>
                <DocumentUploadIcon
                  sx={{
                    height: "42px",
                    width: "42px",
                    marginBottom: "8px",
                  }}
                />
                <Typography variant="body1">
                  <Trans
                    t={t}
                    i18nKey="drag-drop-or-browse"
                    components={{
                      span: <span style={{ color: "var(--green)" }} />,
                    }}
                  />
                </Typography>
                <Typography variant="body1">
                  <Trans
                    t={t}
                    i18nKey="maximum-file-size"
                    values={{
                      size: (MAX_UPLOAD_FILE_SIZE / 1e6).toFixed(0),
                    }}
                  />
                </Typography>
              </Box>
            </Box>
          )}
        </>
      )}

      {selectedOption === "select" && (
        <>
          <Box height={"480px"}>
            <AppDataGrid
              rows={
                filterFileType === "all"
                  ? rows
                  : rows.filter((e) => e.evidenceFileType === filterFileType)
              }
              columns={columns}
              rowHeight={60}
              slots={{ toolbar: Toolbar }}
              localeText={{
                toolbarQuickFilterPlaceholder: `${t("Search-Files")}...`,
                noRowsLabel: `${t("No-Data")}`,
                footerRowSelected: (count) =>
                  t("Row-Selected", { param: count }),
              }}
              pagination
              pageSizeOptions={[25, 50, 100]}
              sx={{
                ...tableStyle(),
              }}
              getRowId={(row) => row.attachmentRef}
              onRowClick={(params) => {
                deleteFile();
                props.setAttachmentRefValue(params.id as string);
              }}
            />
          </Box>
        </>
      )}
    </Box>
  );
};
